import {Opening} from "../../../../../api/Openings";
import {DrawingInfo, ExportColumn} from "../MakeSubmittal";
import React, {PropsWithChildren, useCallback, useEffect, useRef} from "react";
import {pdfTableStyle} from "../../../../../misc/pdf/parts/Page";
import {css} from "@emotion/css";
import {useReadyCheck} from "../../../../../misc/pdf/UseReadyCheck";
import {QuoteOpening} from "../../../../../api/QuoteOpenings";
import {PdfTableServerRendered} from "./PdfTableServerRendered";
import {groupByPhase, phaseLabel} from "../../../../../misc/Phase";
import {screenSlotLabel} from "../screenSlot";

export function OpeningsPage(props: {
    data: (QuoteOpening | Opening)[]
    columns: ExportColumn[]
    drawingInfo: DrawingInfo;
    splitByPhase: boolean;
    onReady(): void;
}) {

    const ready = useReadyCheck(props.onReady);
    ready.require(true);

    const toTable = (list: (QuoteOpening | Opening)[], title?: string) => ({
        title: title,
        header: props.columns.map(c => c.display),
        content: list.map(d => props.columns.map(c => {

            if (c.key === "notes") {
                if(d.doorActiveByOthers || d.doorInactiveByOthers || d.frameByOthers) {
                    let names: string[] = [];
                    const all = d.doorActiveByOthers && d.doorInactiveByOthers && d.frameByOthers;

                    if(d.doorActiveByOthers && d.doorInactiveByOthers) {
                        names.push("Door");
                    } else {
                        if (d.doorActiveByOthers) names.push("Active Door");
                        if (d.doorInactiveByOthers) names.push("Inactive Door");
                    }

                    if(d.frameByOthers) names.push("Frame");

                    let base = d[c.key]?.toString() || "";
                    if(all) {
                        return joinPunct(base, "By others");
                    }

                    return joinPunct(base, names.join(", ") + " by others");
                }
            } else if(c.forFrame && !c.forDoor && d.drwScreenPrimaryOpening) {
                // a secondary of a multi-opening screen uses its primary's frame.
                return "Frame of " + screenSlotLabel(d);
            } else {
                let frameSkippable = c.forFrame ? d.frameByOthers : null;
                let doorSkippable = c.forDoor ? d.doorActiveByOthers && d.doorInactiveByOthers : null;

                if(frameSkippable === null && doorSkippable === null) {
                    // fall through
                } else if(frameSkippable === null) {
                    if(doorSkippable) return "";
                    // fall through
                } else if(doorSkippable === null) {
                    if(frameSkippable) return "";
                    // fall through
                } else if(doorSkippable && frameSkippable) {
                    return ""
                }
            }

            if(c.render) {
                return c.render(d, props.drawingInfo).toString() || "";
            }

            return d[c.key]?.toString() || "";
        }))
    });

    if(props.splitByPhase) {
        return (<>
            {groupByPhase(props.data, d => d.phase).map(section => (
                <PdfTableServerRendered key={section.phase} value={toTable(section.items, phaseLabel(section.phase))} />
            ))}
        </>)
    }

    return (<>
        <PdfTableServerRendered value={toTable(props.data)} />
    </>)
}

function joinPunct(base: string, suffix: string) {
    return base + (base && suffix ? ". " : "") + suffix;
}

export function AutoShrinkTable(props: PropsWithChildren<{ }>) {
    const tableRef = useRef<HTMLTableElement|null>(null);

    const updateHeight = useCallback(() => {
        const t = tableRef.current;
        if(!t) return;

        t.style.marginBottom = ""

        const hSmall = t.getBoundingClientRect().height;

        t.style.transform = "none"
        const hBig = t.getBoundingClientRect().height;

        t.style.transform = ""
        t.style.marginBottom = -(hBig - hSmall)/2 + "px";
        t.style.marginTop = -(hBig - hSmall)/2 + "px";

    }, []);

    useEffect(() => {
        updateHeight();
    });

    return (
        <table className={pdfTableStyle + " " + shrinkOnRender} style={{fontSize: "10px"}} ref={r => {
            tableRef.current = r;

            if(r) {
                updateHeight()
            }
        }}>
            {props.children}
        </table>
    )
}

const shrinkOnRender = css(`
@media print {
    width: 133%;
    transform: scale(0.75) translateX(-16.6%);
}

width: 133%;
transform: scale(0.75);
`)